import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import PropTypes from 'prop-types';
import AnimatedUnderline from '@/components/common/AnimatedUnderline';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import EmailsInput from '@/components/common/EmailsInput';
import translate from '@/lib/intl/translate';
import { GroupType } from '@/propTypes';
import userActionTypes from '@/rdx/actionTypes';
import { extractEmails } from '@/utils/emails';
import InviteSelector from '../Invites/InviteSelector';

const { INVITE_FORM_UPDATE_GROUPS, INVITE_FORM_UPDATE_EMAILS } =
  userActionTypes;

function SendInvitesModal({
  groups,
  handleClose,
  isLoading,
  open,
  sendInvites,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isEmailsInputActive, setChipInputActive] = useState(true);

  const dispatch = useDispatch();

  const { selectedGroups, emails, error } = useSelector((state) => ({
    selectedGroups: state.inviteForm.groups?.length
      ? state.inviteForm?.groups
      : [groups[0]],
    emails: state.inviteForm.emails,
    error: state.inviteForm.error,
  }));

  const updateGroups = (newGroups) =>
    dispatch({
      type: INVITE_FORM_UPDATE_GROUPS,
      groups: newGroups,
    });

  const updateEmails = (newEmails) =>
    dispatch({
      type: INVITE_FORM_UPDATE_EMAILS,
      emails: newEmails,
    });

  const onSendInvites = useCallback(() => {
    const updatedEmails = [...emails, ...extractEmails(inputValue)];

    if (inputValue) {
      updateEmails(updatedEmails);
      setInputValue('');
    }

    sendInvites({
      emails: updatedEmails,
      groups: selectedGroups,
    });
  }, [emails, inputValue, selectedGroups]);

  const onModalClose = useCallback((_e, reason) => {
    if (reason !== 'backdropClick') {
      updateEmails([]);
    }

    handleClose();
  }, []);

  return (
    <Dialog
      aria-labelledby="invite-your-team-dialog"
      fullWidth
      maxWidth="md"
      onClose={onModalClose}
      open={open}
      scroll="paper"
    >
      <DialogHeader
        headerText={translate('COMPANY_PAGE_INVITE_TEAM_LABEL')}
        onClose={onModalClose}
      />
      <DialogContent dividers>
        <div className="text-base font-normal">
          {translate('COMPANY_SEND_INVITES_MODAL_DESCRIPTION')}
        </div>
        <div className="mt-6 flex items-center justify-between">
          <InviteSelector
            handleChange={updateGroups}
            options={groups}
            selectedOptions={selectedGroups}
          />
          {Boolean(emails?.length) && (
            <Button
              variant="text"
              type="button"
              onClick={() => updateEmails([])}
            >
              {translate('CLEAR_ALL_EMAILS')}
            </Button>
          )}
        </div>
        <div className="mt-4 flex max-h-full overflow-y-scroll child:flex-1">
          <EmailsInput
            inputValue={inputValue}
            onInputValueChange={setInputValue}
            values={emails}
            onValuesChange={updateEmails}
            onFocusChange={setChipInputActive}
          />
        </div>
        <AnimatedUnderline
          active={isEmailsInputActive || !!error}
          error={!!error}
        />
      </DialogContent>
      <DialogActions>
        {Boolean(error) && <div className="px-2 text-error-main">{error}</div>}
        <DialogActionButton
          data-testid="send-invites-button"
          onClick={onSendInvites}
          showLoader={isLoading}
        >
          {translate('SEND_INVITE')}
        </DialogActionButton>
        <DialogCancelButton
          data-testid="send-invites-cancel-button"
          onClick={handleClose}
        />
      </DialogActions>
    </Dialog>
  );
}

SendInvitesModal.propTypes = {
  groups: GroupType.isRequired,
  isLoading: PropTypes.bool,
  sendInvites: PropTypes.func.isRequired,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};

SendInvitesModal.defaultProps = {
  open: false,
  handleClose: () => {},
  isLoading: false,
};

export default SendInvitesModal;
